import axios from "axios";
import { markActivity, incrementInFlight, decrementInFlight } from "./idleTracker";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const api = axios.create({
  baseURL: API,
  withCredentials: true,
  headers: { "Content-Type": "application/json" },
});

const isIdleExempt = (cfg) => {
  const url = cfg?.url || "";
  return url.includes("/auth/keepalive") || url.includes("/auth/logout");
};

api.interceptors.request.use((config) => {
  if (!isIdleExempt(config) && !config.background) markActivity();
  if (config.background) config.headers["X-Background"] = "1";
  incrementInFlight();
  return config;
});

api.interceptors.response.use(
  (res) => {
    decrementInFlight();
    if (!isIdleExempt(res.config) && !res.config?.background) markActivity();
    return res;
  },
  (error) => {
    decrementInFlight();
    if (error.response?.status === 401) {
      if (!window.location.pathname.includes("/login")) {
        const detail = String(error.response?.data?.detail || "").toLowerCase();
        window.location.href = detail.includes("inactivite") ? "/login?expired=1" : "/login";
      }
    }
    if (error.response?.status === 400 && error.response?.data?.detail?.includes("selectionner un tenant")) {
      if (!window.location.pathname.includes("/select-tenant")) {
        window.location.href = "/select-tenant";
      }
    }
    return Promise.reject(error);
  }
);

export const authAPI = {
  exchangeSession: (session_id) => api.post("/auth/session", { session_id }),
  me: () => api.get("/auth/me"),
  logout: () => api.post("/auth/logout"),
  login: (email, password) => api.post("/auth/login", { email, password }),
  keepalive: () => api.post("/auth/keepalive"),
};

export const productFamilyAPI = {
  list: () => api.get("/product-families"),
  create: (data) => api.post("/product-families", data),
  update: (id, data) => api.put(`/product-families/${id}`, data),
  delete: (id) => api.delete(`/product-families/${id}`),
  bulkDelete: (ids) => api.post("/product-families/bulk-delete", { ids }),
};

export const productSubFamilyAPI = {
  list: (familyId) => api.get(`/product-sub-families${familyId ? `?family_id=${familyId}` : ""}`),
  create: (data) => api.post("/product-sub-families", data),
  update: (id, data) => api.put(`/product-sub-families/${id}`, data),
  delete: (id) => api.delete(`/product-sub-families/${id}`),
  bulkDelete: (ids) => api.post("/product-sub-families/bulk-delete", { ids }),
};

export const productAPI = {
  list: (params = {}) => {
    const q = new URLSearchParams(params).toString();
    return api.get(`/products${q ? `?${q}` : ""}`);
  },
  create: (data) => api.post("/products", data),
  update: (id, data) => api.put(`/products/${id}`, data),
  delete: (id) => api.delete(`/products/${id}`),
  bulkCreate: (products) => api.post("/products/bulk-create", { products }),
  bulkAction: (ids, action, params) => api.post("/products/bulk-action", { ids, action, params }),
  counts: () => api.get("/products/counts"),
};

export const productContractAPI = {
  list: (clientId, includeArchived = false) => {
    const params = new URLSearchParams();
    if (clientId) params.set("client_id", clientId);
    if (includeArchived) params.set("include_archived", "true");
    const qs = params.toString();
    return api.get(`/product-contracts${qs ? `?${qs}` : ""}`);
  },
  get: (id) => api.get(`/product-contracts/${id}`),
  create: (data) => api.post("/product-contracts", data),
  update: (id, data) => api.put(`/product-contracts/${id}`, data),
  delete: (id) => api.delete(`/product-contracts/${id}`),
  archive: (id) => api.post(`/product-contracts/${id}/archive`),
  unarchive: (id) => api.post(`/product-contracts/${id}/unarchive`),
};

export const clientAPI = {
  list: (params = {}) => {
    const q = new URLSearchParams(params).toString();
    return api.get(`/clients${q ? `?${q}` : ""}`);
  },
  get: (id) => api.get(`/clients/${id}`),
  create: (data) => api.post("/clients", data),
  update: (id, data) => api.put(`/clients/${id}`, data),
  delete: (id) => api.delete(`/clients/${id}`),
  convert: (id) => api.put(`/clients/${id}/convert`),
  bulkAction: (ids, action, params) => api.post("/clients/bulk-action", { ids, action, params }),
  merge: (keepId, mergeIds) => api.post("/clients/merge", { keep_id: keepId, merge_ids: mergeIds }),
  duplicates: () => api.get("/clients/duplicates"),
};

export const supplierAPI = {
  list: (params = {}) => {
    const q = new URLSearchParams(params).toString();
    return api.get(`/suppliers${q ? `?${q}` : ""}`);
  },
  get: (id) => api.get(`/suppliers/${id}`),
  create: (data) => api.post("/suppliers", data),
  update: (id, data) => api.put(`/suppliers/${id}`, data),
  delete: (id) => api.delete(`/suppliers/${id}`),
  match: (params) => {
    const q = new URLSearchParams(params).toString();
    return api.get(`/suppliers/match?${q}`);
  },
  bulkAction: (ids, action, params) => api.post("/suppliers/bulk-action", { ids, action, params }),
};

export const documentAPI = {
  list: (params = {}) => {
    const q = new URLSearchParams(params).toString();
    return api.get(`/documents${q ? `?${q}` : ""}`);
  },
  get: (id) => api.get(`/documents/${id}`),
  create: (data) => api.post("/documents", data),
  update: (id, data) => api.put(`/documents/${id}`, data),
  delete: (id) => api.delete(`/documents/${id}`),
  emit: (id) => api.post(`/documents/${id}/emit`),
  duplicate: (id) => api.post(`/documents/${id}/duplicate`),
  bulkAction: (ids, action, params) => api.post("/documents/bulk-action", { ids, action, params }),
  listPayments: (id) => api.get(`/documents/${id}/payments`),
  addPayment: (id, data) => api.post(`/documents/${id}/payments`, data),
  updatePayment: (docId, paymentId, data) => api.put(`/documents/${docId}/payments/${paymentId}`, data),
  deletePayment: (docId, paymentId) => api.delete(`/documents/${docId}/payments/${paymentId}`),
  updateDueDate: (id, due_date) => api.put(`/documents/${id}/due-date`, { due_date }),
  getSendHistory: (id) => api.get(`/documents/${id}/send-history`),
  addSendHistory: (id, data) => api.post(`/documents/${id}/send-history`, data),
  signDevis: (id) => api.post(`/documents/${id}/sign`),
  acompteSummary: (id) => api.get(`/documents/${id}/acompte-summary`),
  createAcompte: (id, data) => api.post(`/documents/${id}/create-acompte`, data),
  createSolde: (id, data = {}) => api.post(`/documents/${id}/create-solde`, data),
};

export const supplierOrderAPI = {
  list: (supplierId) => api.get(`/supplier-orders${supplierId ? `?supplier_id=${supplierId}` : ""}`),
  create: (data) => api.post("/supplier-orders", data),
  update: (id, data) => api.put(`/supplier-orders/${id}`, data),
  delete: (id) => api.delete(`/supplier-orders/${id}`),
};

export const supplierQuoteAPI = {
  upload: (file, supplierId) => {
    const formData = new FormData();
    formData.append("file", file);
    formData.append("supplier_id", supplierId || "");
    return api.post("/supplier-quotes/upload", formData, { headers: { "Content-Type": "multipart/form-data" } });
  },
  analyze: (file) => {
    const formData = new FormData();
    formData.append("file", file);
    return api.post("/supplier-quotes/analyze", formData, { headers: { "Content-Type": "multipart/form-data" } });
  },
  confirm: (data) => api.post("/supplier-quotes/confirm", data),
  list: (supplierId) => api.get(`/supplier-quotes${supplierId ? `?supplier_id=${supplierId}` : ""}`),
  get: (id) => api.get(`/supplier-quotes/${id}`),
  updateItems: (id, items) => api.put(`/supplier-quotes/${id}/items`, { items }),
  createDevis: (id, clientId) => api.post(`/supplier-quotes/${id}/create-devis`, { client_id: clientId }),
};

export const dashboardAPI = {
  get: (params = {}) => {
    const q = new URLSearchParams(params).toString();
    return api.get(`/dashboard${q ? `?${q}` : ""}`);
  },
};

export const searchAPI = {
  query: (q) => api.get(`/search?q=${encodeURIComponent(q)}&limit=8`),
};

export const statsAPI = {
  get: () => api.get("/stats"),
  tva: (params) => api.get("/stats/tva", { params }),
};

export const adminAPI = {
  getSettings: () => api.get("/admin/settings"),
  updateSettings: (data) => api.put("/admin/settings", data),
  listPaymentMethods: () => api.get("/admin/payment-methods"),
  addPaymentMethod: (value, label) => api.post("/admin/payment-methods", { value, label }),
  renamePaymentMethodLabel: (value, label) => api.patch(`/admin/payment-methods/${encodeURIComponent(value)}`, { label }),
  deletePaymentMethod: (value, force = false) => api.delete(`/admin/payment-methods/${encodeURIComponent(value)}${force ? "?force=true" : ""}`),
  setDefaultPaymentMethod: (value) => api.post("/admin/payment-methods/default", { value }),
  listAllowedEmails: () => api.get("/admin/allowed-emails"),
  addAllowedEmail: (email) => api.post("/admin/allowed-emails", { email }),
  removeAllowedEmail: (email) => api.delete(`/admin/allowed-emails/${encodeURIComponent(email)}`),
  listUsers: () => api.get("/admin/users"),
  updateUserRole: (userId, role) => api.put(`/admin/users/${userId}/role`, { role }),
  updateUser: (userId, data) => api.put(`/admin/users/${userId}`, data),
  uploadLogo: (file) => {
    const formData = new FormData();
    formData.append("file", file);
    return api.post("/admin/logo", formData, { headers: { "Content-Type": "multipart/form-data" } });
  },
  listTenants: () => api.get("/admin/tenants"),
  createTenant: (data) => api.post("/admin/tenants", data),
  updateTenant: (id, data) => api.put(`/admin/tenants/${id}`, data),
  getTenantSettings: (id) => api.get(`/admin/tenants/${id}/settings`),
  deactivateTenant: (id) => api.delete(`/admin/tenants/${id}`),
  reactivateTenant: (id) => api.post(`/admin/tenants/${id}/reactivate`),
  updateTenantModules: (id, modules) => api.put(`/admin/tenants/${id}/modules`, { modules }),
  getDashboard: () => api.get("/admin/dashboard"),
  accessTenant: (id) => api.post(`/admin/tenants/${id}/access`),
  createTenantUser: (tenantId, data) => api.post(`/admin/tenants/${tenantId}/users`, data),
  listTenantUsers: (tenantId) => api.get(`/admin/tenants/${tenantId}/users`),
  updateTenantUserRole: (tenantId, userId, role) => api.put(`/admin/tenants/${tenantId}/users/${userId}`, { role_in_tenant: role }),
  removeTenantUser: (tenantId, userId) => api.delete(`/admin/tenants/${tenantId}/users/${userId}`),
  getMyTenant: () => api.get("/admin/my-tenant"),
  toggleImport: (enabled) => api.put("/admin/my-tenant/import-toggle", { import_enabled: enabled }),
  getBranding: () => api.get("/admin/branding"),
  updateBranding: (data) => api.put("/admin/branding", data),
  uploadBrandingLogo: (file) => {
    const formData = new FormData();
    formData.append("file", file);
    return api.post("/admin/branding/logo", formData, { headers: { "Content-Type": "multipart/form-data" } });
  },
  deleteUser: (userId) => api.delete(`/admin/users/${userId}/permanent`),
  deleteTenant: (tenantId) => api.delete(`/admin/tenants/${tenantId}/permanent`),
  purgeTenant: (tenantId, confirmSlug) => api.post(`/admin/tenants/${tenantId}/purge`, { confirm_slug: confirmSlug }),
};

export const companyLookupAPI = {
  search: (q) => api.get("/company-lookup", { params: { q } }),
};

export const tenantAPI = {
  list: () => api.get("/tenants"),
  select: (tenantId) => api.post("/tenants/select", { tenant_id: tenantId }),
};

export const billingAPI = {
  forecast: () => api.get("/billing-forecast"),
  generate: (data) => api.post("/billing-forecast/generate", data),
  cancelInvoice: (docId) => api.post("/billing-forecast/cancel-invoice", { doc_id: docId }),
};

export const aiAPI = {
  clientSummary: (clientId) => api.get(`/ai/client-summary/${clientId}`),
  followUpSuggestions: () => api.get("/ai/follow-up-suggestions"),
  anomalies: () => api.get("/ai/anomalies"),
  enrichClient: (clientId) => api.post(`/ai/enrich-client/${clientId}`),
  applyEnrichment: (clientId, fields) => api.put(`/ai/enrich-client/${clientId}/apply`, { fields }),
  chat: (message, history) => api.post("/ai/chat", { message, history }),
};

export const pdfAPI = {
  download: (docId, printAs = null) => api.get(`/documents/${docId}/pdf${printAs ? `?print_as=${printAs}` : ""}`, { responseType: "blob" }),
  sendEmail: (docId, email, opts = {}) => api.post(`/documents/${docId}/send-email`, { email, ...opts }),
};

export const convertAPI = {
  convert: (docId, targetType) => api.post(`/documents/${docId}/convert`, { target_type: targetType }),
};

export const expenseNoteAPI = {
  list: (params = {}) => {
    const q = new URLSearchParams(params).toString();
    return api.get(`/expense-notes${q ? `?${q}` : ""}`);
  },
  get: (id) => api.get(`/expense-notes/${id}`),
  getTicket: (id) => api.get(`/expense-notes/${id}/ticket`),
  scan: (file) => {
    const fd = new FormData();
    fd.append("file", file);
    return api.post("/expense-notes/scan", fd, { headers: { "Content-Type": "multipart/form-data" } });
  },
  create: (data, force = false) => api.post(`/expense-notes${force ? "?force=true" : ""}`, data),
  update: (id, data) => api.put(`/expense-notes/${id}`, data),
  delete: (id) => api.delete(`/expense-notes/${id}`),
};

export const importAPI = {
  upload: (entity, file) => {
    const formData = new FormData();
    formData.append("file", file);
    return api.post(`/import/${entity}`, formData, { headers: { "Content-Type": "multipart/form-data" } });
  },
  status: () => api.get("/import/status"),
};

export const bankStatementAPI = {
  parse: (file, force = false) => {
    const fd = new FormData();
    fd.append("file", file);
    const qs = force ? "?force=true" : "";
    return api.post(`/bank-statements/parse${qs}`, fd, { headers: { "Content-Type": "multipart/form-data" } });
  },
  parseStatus: (jobId) => api.get(`/bank-statements/parse-status/${jobId}`),
  match: (transactions) => api.post("/bank-statements/match", { transactions }),
  validate: (data) => api.post("/bank-statements/validate", data),
  list: () => api.get("/bank-statements"),
  get: (id) => api.get(`/bank-statements/${id}`),
  clientUnpaid: (clientId) => api.get(`/clients/${clientId}/unpaid-invoices`),
  clientUnassigned: (clientId) => api.get(`/clients/${clientId}/bank-transactions`),
  clientRelatedUnpaid: (clientId) => api.get(`/clients/${clientId}/related-unpaid-invoices`),
  clientGroupSummary: (clientId, dateFrom, dateTo) => {
    const params = new URLSearchParams();
    if (dateFrom) params.append("date_from", dateFrom);
    if (dateTo) params.append("date_to", dateTo);
    const qs = params.toString();
    return api.get(`/clients/${clientId}/group-summary${qs ? "?" + qs : ""}`);
  },
  searchInvoices: (q) => api.get(`/invoices/search?q=${encodeURIComponent(q)}`),
  listAliases: () => api.get("/bank-aliases"),
  createAlias: (data) => api.post("/bank-aliases", data),
  deleteAlias: (id) => api.delete(`/bank-aliases/${id}`),
  debits: (params = {}) => api.get("/bank-statements/debits", { params }),
  debitCategories: () => api.get("/bank-statements/categories"),
  categorizeDebits: () => api.post("/bank-statements/categorize-debits"),
  categorizeStatus: () => api.get("/bank-statements/categorize-status"),
  compteRésultat: (params) => api.get("/bank-statements/compte-resultat", { params }),
  updateDebitCategory: (statementId, debitIdx, category) => api.put(`/bank-statements/${statementId}/debit/${debitIdx}/category`, { category }),
  extractDebits: (file) => {
    const fd = new FormData();
    fd.append("file", file);
    return api.post("/bank-statements/extract-debits", fd, { headers: { "Content-Type": "multipart/form-data" } });
  },
  addManualDebit: (data) => api.post("/bank-statements/debits/manual", data),
  deleteDebit: (statementId, debitId) => api.delete(`/bank-statements/${statementId}/debits/${debitId}`),
  deleteStatement: (statementId) => api.delete(`/bank-statements/${statementId}`),
  reextract: (statementId) => api.post(`/bank-statements/${statementId}/reextract`),
  // Drafts (in-progress reconciliations, auto-saved every 2s)
  upsertDraft: (data) => api.post("/bank-statements/drafts", data),
  listDrafts: () => api.get("/bank-statements/drafts"),
  getDraft: (id) => api.get(`/bank-statements/drafts/${id}`),
  deleteDraft: (id) => api.delete(`/bank-statements/drafts/${id}`),
  // Reconciliation: bank debit ↔ supplier invoice
  suggestSupplierInvoice: (debitId) => api.get(`/bank-statements/debits/${debitId}/suggest-supplier-invoice`),
  linkDebitToSupplierInvoice: (debitId, supplierInvoiceId) => api.post(`/bank-statements/debits/${debitId}/link-supplier-invoice`, { supplier_invoice_id: supplierInvoiceId }),
  unlinkDebitFromSupplierInvoice: (debitId) => api.delete(`/bank-statements/debits/${debitId}/link-supplier-invoice`),
  updateDebitCategoryById: (debitId, category) => api.put(`/bank-statements/debits/${debitId}/category`, { category }),
  resumeStatementAsDraft: (statementId) => api.post(`/bank-statements/${statementId}/resume-as-draft`),
  // Mark/unmark multiple debits as "charge sans facture" (accrual mode only)
  bulkMarkCharge: (debitIds, isCharge) => api.post("/bank-statements/debits/bulk-mark-charge", { debit_ids: debitIds, is_charge: isCharge }),
  bulkSetExcluded: (debitIds, excluded) => api.post("/bank-statements/debits/bulk-exclude", { debit_ids: debitIds, excluded }),
  detectDuplicates: (params) => api.get("/bank-statements/compte-resultat/duplicates", { params }),
  confirmDuplicate: (debitId, invoiceId) => api.post("/bank-statements/compte-resultat/duplicates/confirm", { debit_id: debitId, invoice_id: invoiceId }),
  dismissDuplicate: (debitId, invoiceId) => api.post("/bank-statements/compte-resultat/duplicates/dismiss", { debit_id: debitId, invoice_id: invoiceId }),
  exportCompteResultatPdf: (params) => api.get("/bank-statements/compte-resultat-pdf", { params, responseType: "blob" }),
  downloadPdf: (statementId) => api.get(`/bank-statements/${statementId}/pdf`, { responseType: "blob" }),
};

export const compteCourantAssocieAPI = {
  get: (params = {}) => {
    const q = new URLSearchParams();
    Object.entries(params).forEach(([k, v]) => { if (v != null && v !== "") q.append(k, v); });
    const qs = q.toString();
    return api.get(`/compte-courant-associe${qs ? `?${qs}` : ""}`);
  },
};

export const manualPaymentAPI = {
  create: (data) => api.post("/manual-payments", data),
  list: (clientId) => api.get(`/manual-payments${clientId ? `?client_id=${clientId}` : ""}`),
};

export const supplierInvoiceAPI = {
  // Personal payment (associé current account)
  markPaidPersonally: (id, data) => api.post(`/supplier-invoices/${id}/mark-paid-personally`, data),
  unmarkPaidPersonally: (id) => api.post(`/supplier-invoices/${id}/unmark-paid-personally`),
  analyze: (file) => {
    const formData = new FormData();
    formData.append("file", file);
    return api.post("/supplier-invoices/analyze", formData, { headers: { "Content-Type": "multipart/form-data" }, timeout: 120000 });
  },
  analyzeStatus: (jobId) => api.get(`/supplier-invoices/analyze-status/${jobId}`),
  create: (data) => api.post("/supplier-invoices", data),
  list: (params = {}) => {
    const q = new URLSearchParams(params).toString();
    return api.get(`/supplier-invoices${q ? `?${q}` : ""}`);
  },
  get: (id) => api.get(`/supplier-invoices/${id}`),
  update: (id, data) => api.put(`/supplier-invoices/${id}`, data),
  delete: (id) => api.delete(`/supplier-invoices/${id}`),
  types: () => api.get("/supplier-invoices/types/list"),
  checkSupplier: (name) => api.get(`/suppliers/check-name?name=${encodeURIComponent(name)}`),
  repairSuppliers: () => api.post("/supplier-invoices/repair-suppliers"),
};

export const marginAnalysisAPI = {
  overview: (year) => api.get(`/margin-analysis/overview?year=${year}`),
  pricing: (targetMargin = 20) => api.get(`/margin-analysis/pricing?target_margin=${targetMargin}`),
  parse: (file) => {
    const formData = new FormData();
    formData.append("file", file);
    return api.post("/margin-analysis/parse", formData, { headers: { "Content-Type": "multipart/form-data" }, timeout: 120000 });
  },
  save: (data) => api.post("/margin-analysis", data),
  list: () => api.get("/margin-analysis"),
  get: (id) => api.get(`/margin-analysis/${id}`),
  updateMatch: (id, data) => api.put(`/margin-analysis/${id}/match`, data),
  delete: (id) => api.delete(`/margin-analysis/${id}`),
};

export default api;

export const campaignAPI = {
  list: (params) => api.get("/campaigns", { params }),
  get: (id) => api.get(`/campaigns/${id}`),
  create: (data) => api.post("/campaigns", data),
  send: (id) => api.post(`/campaigns/${id}/send`),
  delete: (id) => api.delete(`/campaigns/${id}`),
  previewCount: (id) => api.get(`/campaigns/${id}/preview-count`),
};

export const mailingListAPI = {
  list: () => api.get("/mailing-lists"),
  get: (id) => api.get(`/mailing-lists/${id}`),
  create: (data) => api.post("/mailing-lists", data),
  update: (id, data) => api.put(`/mailing-lists/${id}`, data),
  addContacts: (id, contactIds) => api.post(`/mailing-lists/${id}/add-contacts`, { contact_ids: contactIds }),
  removeContacts: (id, contactIds) => api.post(`/mailing-lists/${id}/remove-contacts`, { contact_ids: contactIds }),
  delete: (id) => api.delete(`/mailing-lists/${id}`),
};

export const emailTemplateAPI = {
  list: () => api.get("/email-templates"),
  get: (id) => api.get(`/email-templates/${id}`),
  create: (data) => api.post("/email-templates", data),
  update: (id, data) => api.put(`/email-templates/${id}`, data),
  delete: (id) => api.delete(`/email-templates/${id}`),
};


export const taskAPI = {
  list: (showAll = false, config = {}) => api.get(`/tasks${showAll ? "?show_all=true" : ""}`, config),
  create: (data) => api.post("/tasks", data),
  update: (id, data) => api.put(`/tasks/${id}`, data),
  toggle: (id) => api.patch(`/tasks/${id}/toggle`),
  delete: (id) => api.delete(`/tasks/${id}`),
  users: () => api.get("/tasks/users"),
};

export const exportAPI = {
  documentsUrl: (params) => `${API}/export/documents?${new URLSearchParams(params)}`,
  clientsUrl: (params) => `${API}/export/clients?${new URLSearchParams(params)}`,
};

export const comptabiliteAPI = {
  fecUrl: (params) => `${API}/comptabilite/export-fec?${new URLSearchParams(params)}`,
  importFec: (formData) => api.post("/comptabilite/import-fec", formData, { headers: { "Content-Type": "multipart/form-data" } }),
  importBilanPdf: (formData) => api.post("/comptabilite/import-bilan-pdf", formData, {
    headers: { "Content-Type": "multipart/form-data" },
    timeout: 180000, // Bilan AI extraction can take up to ~60s
  }),
  generateAnFec: (payload) => api.post("/comptabilite/generate-an-fec", payload, { responseType: "blob" }),
  getReportANouveau: (exercice) => api.get(`/comptabilite/report-a-nouveau${exercice ? `?exercice=${exercice}` : ""}`),
  saveReportANouveau: (data) => api.post("/comptabilite/report-a-nouveau", data),
  deleteReportANouveau: (exercice) => api.delete(`/comptabilite/report-a-nouveau/${exercice}`),
};

